/**
 * Contact Modal Component
 * Handles open/close, focus trap, and AJAX submit.
 */

class ContactModal {
    REQUIRED_FIELDS_MESSAGE = 'Please complete all required fields.';
    REQUIRED_WITH_PRIVACY_MESSAGE = 'All fields are required. Continue by accepting the Privacy Policy.';
    modal;
    dialog;
    closeButtons;
    form;
    status;
    requiredFields = [];
    isOpen = false;
    lastActiveElement = null;
    isSubmitting = false;
    statusTimer = null;
    successTimer = null;

    constructor() {
        this.modal = document.querySelector('.contact-modal');
        if (!this.modal) return;

        this.dialog = this.modal.querySelector('.contact-modal__dialog');
        this.closeButtons = this.modal.querySelectorAll('.js-contact-modal-close');
        this.form = this.modal.querySelector('.contact-modal__form');
        this.status = this.modal.querySelector('.contact-modal__status');
        this.requiredFields = this.form ? Array.from(this.form.querySelectorAll('[required]')).filter((field) => field.type !== 'hidden') : [];

        this.init();
    }

    init() {
        document.addEventListener('openContactModal', () => {
            this.open();
        });

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-modal="contact"], .js-contact-modal-trigger');
            if (!trigger) return;
            event.preventDefault();
            this.open();
            this.closeMobileMenuIfOpen();
        });

        this.closeButtons.forEach((button) => {
            button.addEventListener('click', () => this.close());
        });

        this.modal.addEventListener('click', (event) => {
            if (event.target === this.modal) {
                this.close();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (!this.isOpen) return;
            if (event.key === 'Escape') {
                this.close();
                return;
            }
            if (event.key === 'Tab') {
                this.trapFocus(event);
            }
        });

        if (this.form) {
            this.form.addEventListener('submit', (event) => this.handleSubmit(event));
            this.requiredFields.forEach((field) => {
                const eventName = field.type === 'checkbox' ? 'change' : 'input';
                field.addEventListener(eventName, () => this.clearFieldError(field));
            });
        }
    }

    getConsentLabel(checkbox) {
        if (!this.form || !checkbox?.id) return null;
        return this.form.querySelector(`label[for="${checkbox.id}"]`);
    }

    clearFieldError(field) {
        field.classList.remove('is-error');
        if (field.type === 'checkbox') {
            this.getConsentLabel(field)?.classList.remove('is-error');
        }
    }

    clearAllFieldErrors() {
        this.requiredFields.forEach((field) => this.clearFieldError(field));
    }

    getValidationState() {
        const missingInputs = [];
        let missingConsent = false;

        this.requiredFields.forEach((field) => {
            if (field.type === 'checkbox') {
                if (!field.checked) {
                    missingConsent = true;
                }
                return;
            }

            if (!String(field.value || '').trim()) {
                missingInputs.push(field);
            }
        });

        return {
            missingInputs,
            missingConsent,
            hasErrors: missingInputs.length > 0 || missingConsent
        };
    }

    applyValidationErrors({ missingInputs, missingConsent }) {
        this.clearAllFieldErrors();
        missingInputs.forEach((field) => field.classList.add('is-error'));

        if (missingConsent) {
            const consentField = this.requiredFields.find((field) => field.type === 'checkbox');
            if (consentField) {
                consentField.classList.add('is-error');
                this.getConsentLabel(consentField)?.classList.add('is-error');
            }
        }
    }

    getValidationMessage({ missingConsent }) {
        return missingConsent ? this.REQUIRED_WITH_PRIVACY_MESSAGE : this.REQUIRED_FIELDS_MESSAGE;
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.lastActiveElement = document.activeElement;

        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('contact-modal-open');

        const focusTarget = this.modal.querySelector('input, textarea, select, button');
        if (focusTarget) {
            window.setTimeout(() => focusTarget.focus(), 50);
        }
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;

        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('contact-modal-open');

        if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
            this.lastActiveElement.focus();
        }
    }

    trapFocus(event) {
        if (!this.dialog) return;

        const focusable = this.dialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex=\"-1\"])'
        );

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        if (!this.form || this.isSubmitting) return;

        const validation = this.getValidationState();
        if (validation.hasErrors) {
            this.applyValidationErrors(validation);
            this.setStatus(this.getValidationMessage(validation), 'error');
            return;
        }

        this.isSubmitting = true;
        this.setStatus('');
        this.clearAllFieldErrors();

        const submitButton = this.form.querySelector('button[type=\"submit\"]');
        const originalText = submitButton?.textContent;
        const endpoint = this.form.getAttribute('action') || window.ajax_var?.url || '';
        if (!endpoint) {
            this.setStatus('There was a problem sending your request. Please try again.', 'error');
            this.isSubmitting = false;
            return;
        }

        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = 'Sending...';
        }

        try {
            const formData = new FormData(this.form);
            formData.set('page_url', window.location.href);

            const response = await fetch(endpoint, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                const message = data?.data?.message || 'There was a problem sending your request. Please try again.';
                if (
                    message === this.REQUIRED_FIELDS_MESSAGE
                    || message === this.REQUIRED_WITH_PRIVACY_MESSAGE
                ) {
                    const serverValidation = this.getValidationState();
                    if (serverValidation.hasErrors) {
                        this.applyValidationErrors(serverValidation);
                    }
                }
                this.setStatus(message, 'error');
                return;
            }

            this.showSuccessState();
            this.form.reset();
            this.clearAllFieldErrors();
        } catch (error) {
            this.setStatus('There was a problem sending your request. Please try again.', 'error');
        } finally {
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = originalText || 'Submit Request';
            }
            this.isSubmitting = false;
        }
    }

    setStatus(message, type = '') {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.dataset.type = type;
        if (this.form) {
            if (message) {
                this.form.classList.add('is-status-visible');
            } else {
                this.form.classList.remove('is-status-visible');
            }
        }

        if (this.statusTimer) {
            window.clearTimeout(this.statusTimer);
            this.statusTimer = null;
        }

        if (message) {
            this.statusTimer = window.setTimeout(() => {
                this.setStatus('', '');
            }, 3000);
        }
    }

    showSuccessState() {
        if (!this.modal) return;
        this.modal.classList.add('is-success');

        if (this.successTimer) {
            window.clearTimeout(this.successTimer);
        }

        this.successTimer = window.setTimeout(() => {
            this.modal.classList.remove('is-success');
        }, 3000);
    }

    closeMobileMenuIfOpen() {
        const mobileMenu = document.querySelector('.mobile-menu');
        if (!mobileMenu?.classList.contains('is-open')) return;
        const toggleButton = document.querySelector('.js-mobile-menu-toggle');
        toggleButton?.click();
    }
}

const initContactModal = () => {
    new ContactModal();
};

export { initContactModal };
